const BlogInsights = () => (
  <>
    {/* ── EDUCATIE ─────────────────────────────────────────────────── */}
    <section className="py-20 bg-[#080015]">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-3">✦ Kennis ✦</p>
          <h2 className="text-3xl font-bold text-white mb-4">
            Waarom geuren, stenen en energie<br />
            <span className="text-violet-400">werkelijk invloed hebben</span>
          </h2>
          <p className="text-white/50 text-sm max-w-2xl mx-auto">
            Sjamanisme erkent al 40.000 jaar dat onze zintuigen en omgeving direct onze energetische staat beïnvloeden.
            Hier is wat het ons leert — praktisch en zonder mystiek gedoe.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
          {[
            {
              icon: 'ri-aromatherapy-line',
              color: 'violet',
              title: 'Geuren',
              subtitle: 'Invloed op rust en alertheid',
              points: ['Hing verdrijft aantoonbaar agressieve energie', 'Salie reinigt de lucht en stemt de geest', 'Lobhan kalmeert overprikkelde zintuigen', 'Geur bereikt het limbisch systeem direct'],
            },
            {
              icon: 'ri-palette-line',
              color: 'fuchsia',
              title: 'Kleuren',
              subtitle: 'Effect op stemming en energie',
              points: ['Blauw kalmeert en beschermt het energieveld', 'Wit reinigt en neutraliseert negatieve lading', 'Diep indigo versterkt het beschermend bewustzijn', 'Kleuren communiceren direct met de aura'],
            },
            {
              icon: 'ri-gem-line',
              color: 'rose',
              title: 'Stenen & mineralen',
              subtitle: 'Ondersteuning van het energieveld',
              points: ['Abaloneschelp versterkt de beschermende intentie', 'Houtskool absorbeert negatieve lading', 'Mineralen hebben een frequentie die resoneert', 'Traditioneel gebruik al duizenden jaren bewezen'],
            },
            {
              icon: 'ri-flow-chart', 
              color: 'amber',
              title: 'Energie',
              subtitle: 'Constante interactie',
              points: ['Energie is in constante wisselwerking met omgeving', 'Elke persoon draagt een energetisch veld', 'Negatieve energie stapelt op in ruimtes', 'Reiniging herstelt de energetische balans'],
            },
          ].map(item => {
            const colors: Record<string, string> = {
              violet: 'bg-violet-600/20 text-violet-400 border-violet-500/20',
              fuchsia: 'bg-fuchsia-600/20 text-fuchsia-400 border-fuchsia-500/20',
              rose: 'bg-rose-600/20 text-rose-400 border-rose-500/20',
              amber: 'bg-amber-600/20 text-amber-400 border-amber-500/20',
            };
            return (
              <div key={item.title} className={`p-5 border rounded-2xl ${colors[item.color].split(' ').filter(c => c.startsWith('border')).join(' ')} bg-white/3`}>
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl mb-3 ${colors[item.color].split(' ').filter(c => !c.startsWith('border')).join(' ')}`}>
                  <i className={`${item.icon} text-xl`} />
                </div>
                <h3 className="text-white font-bold text-base mb-1">{item.title}</h3>
                <p className="text-white/40 text-xs mb-3">{item.subtitle}</p>
                <ul className="space-y-1.5">
                  {item.points.map(pt => (
                    <li key={pt} className="flex items-start gap-2">
                      <i className="ri-checkbox-blank-circle-fill text-violet-400/50 text-xs mt-1 shrink-0" />
                      <span className="text-white/50 text-xs leading-relaxed">{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* ── GEVOELIGHEID ─────────────────────────────────────────── */}
        <div className="mb-0">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Wie is het meest gevoelig?</h2>
            <p className="text-white/45 text-sm max-w-xl mx-auto">
              Energetische gevoeligheid verschilt per persoon en leeftijd. Sommige groepen zijn structureel kwetsbaarder.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                icon: 'ri-bear-smile-line',
                title: 'Baby\'s en jonge kinderen',
                desc: 'Hebben geen ontwikkeld energetisch afweermechanisme. Absorberen alles: emoties, spanning, negatieve intenties van bezoekers en zelfs de energie van de ruimte zelf.',
                level: 'Extreem gevoelig',
                levelColor: 'text-rose-300 bg-rose-500/10 border-rose-500/20',
              },
              {
                icon: 'ri-heart-pulse-line',
                title: 'Empatische personen',
                desc: 'Mensen die van nature sterk meevoelen met anderen zijn energetisch poreus — ze nemen andermans energie over zonder het te beseffen. Dit leidt tot chronische vermoeidheid.',
                level: 'Sterk gevoelig',
                levelColor: 'text-violet-300 bg-violet-500/10 border-violet-500/20',
              },
              {
                icon: 'ri-building-2-line',
                title: 'Ondernemers',
                desc: 'Dagelijks contact met grote aantallen mensen maakt ondernemers kwetsbaar voor energetische overbelasting. Nijd, concurrentie en zakelijke jaloezie zijn reële bedreigingen.',
                level: 'Sterk gevoelig',
                levelColor: 'text-fuchsia-300 bg-fuchsia-500/10 border-fuchsia-500/20',
              },
            ].map(item => (
              <div key={item.title} className="p-6 bg-white/3 border border-violet-500/10 rounded-2xl">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-violet-600/20 mb-3">
                  <i className={`${item.icon} text-violet-400 text-xl`} />
                </div>
                <span className={`inline-block px-2 py-0.5 border text-xs rounded-full mb-3 font-semibold whitespace-nowrap ${item.levelColor}`}>
                  {item.level}
                </span>
                <h3 className="text-white font-bold text-base mb-2">{item.title}</h3>
                <p className="text-white/45 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ── PRAKTISCHE TIPS ──────────────────────────────────────────── */}
    <section className="py-20 bg-gradient-to-b from-[#080015] to-[#050010]">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-3">✦ Direct toepasbaar ✦</p>
            <h2 className="text-3xl font-bold text-white mb-4">
              Praktische tips die<br />
              <span className="text-violet-400">je vandaag kunt toepassen</span>
            </h2>
            <p className="text-white/50 text-sm leading-relaxed mb-6">
              Bescherming begint met bewustzijn. Hier zijn concrete stappen die elk gezin direct kan toepassen —
              voor betere nachtrust, minder spanning en een positieve thuisomgeving.
            </p>
            <div className="space-y-4">
              {[
                { num: '01', title: 'Rust in de slaapomgeving', desc: 'Zorg voor een energetisch schone slaapkamer. Beperk het aantal bezoekers in baby- en kinderkamers. Reinig de kamer regelmatig met hing poeder of spray.' },
                { num: '02', title: 'Dagelijkse energiereiniging', desc: 'Gebruik elke ochtend een beschermingszeep of spray. Dit hoeft niet meer dan 2 minuten te kosten maar bouwt een krachtige dagelijkse beschermingsroutine op.' },
                { num: '03', title: 'Grenzen bewaken na bezoek', desc: 'Na intensief familiebezoek of sociale verplichtingen: reinig jezelf en je huis. Zeker bij kinderen die erna onrustig zijn.' },
                { num: '04', title: 'Prikkels bewust verminderen', desc: 'Beperk overprikkeling voor kinderen na drukke momenten. Stilte, een vaste routine en een beschermde slaapomgeving zijn de basis.' },
              ].map(tip => (
                <div key={tip.num} className="flex gap-4">
                  <span className="text-3xl font-black text-violet-500/20 shrink-0 leading-none w-8">{tip.num}</span>
                  <div>
                    <h3 className="text-white font-semibold text-sm mb-1">{tip.title}</h3>
                    <p className="text-white/45 text-xs leading-relaxed">{tip.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative h-[500px] rounded-3xl overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/2d1324db31d62503ade6cb6d8d1a0cab.jpg"
              alt="Rustig beschermd thuis"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050010]/70 via-transparent to-transparent" />
          </div>
        </div>
      </div>
    </section>
  </>
);

export default BlogInsights;
